import { useCallback, useEffect, useMemo } from 'react'

import { useAuth0 } from '@auth0/auth0-react'
import useLoggedNavigate from 'hooks/useLoggedNavigate'
import { Button, Dialog, DialogActions } from '@mui/material'
import {
  BottomContent,
  ButtonContainer,
  DialogContentCenterbody,
  Title,
  UnauthorizedDialog,
} from './UnauthorizedAccess.styled'
import LockPersonIcon from '@mui/icons-material/LockPerson'
import useAccessPermissions from 'hooks/useAccessToken/useAccessPermissions'
import { XAPIPermissions } from '../../types/custom.d.ts'

type TProps = {
  requiredPermissions?: Set<XAPIPermissions>
  hasAccess?: (permissions: Set<XAPIPermissions>) => boolean
}

function UnauthorizedAccess(props: TProps) {
  const requiredPermissions = useMemo(
    () => props.requiredPermissions ?? new Set([XAPIPermissions.Access]),
    [props.requiredPermissions]
  )

  const auth0 = useAuth0()
  const permissions = useAccessPermissions()
  const { isAuthenticated, isLoading, logout } = auth0
  const nav = useLoggedNavigate()

  const hasAccessPermission = useCallback(() => {
    if (!permissions.loaded) {
      return false
    }

    return permissions.permissions.has(XAPIPermissions.Access)
  }, [permissions.permissions, permissions.loaded])

  const hasRequiredPermission = useCallback(() => {
    if (!permissions.loaded) {
      return false
    }

    if (props.hasAccess) {
      return props.hasAccess(permissions.permissions)
    }

    return (
      requiredPermissions.intersection(permissions.permissions).size ===
      requiredPermissions.size
    )
  }, [
    permissions.loaded,
    permissions.permissions,
    requiredPermissions,
    props.hasAccess,
  ])

  useEffect(() => {
    if (
      !isLoading &&
      isAuthenticated &&
      permissions.loaded &&
      hasRequiredPermission()
    ) {
      nav('/')
    }
  })

  const login = useCallback(() => {
    console.log('Logging Out - Unauthorized Access Account Change')
    logout()
  }, [logout])

  const goBack = useCallback(() => {
    nav(-1)
  }, [nav])

  console.log(
    isLoading,
    permissions.loaded,
    isAuthenticated,
    hasRequiredPermission()
  )

  if (
    isLoading ||
    !permissions.loaded ||
    (isAuthenticated && hasRequiredPermission())
  ) {
    return null
  }

  return (
    <Dialog open={true}>
      {/* <Subtitles>Access Not Permitted</Subtitles> */}
      <Title>Unauthorized</Title>
      <UnauthorizedDialog>
        <DialogContentCenterbody>
          <LockPersonIcon style={{ fontSize: '128px' }} />
        </DialogContentCenterbody>
        <BottomContent>
          This account is not authorized to access this page. To request access,
          or if you believe this is a mistake, please{' '}
          <a
            //   className={Styles.standardAnchor}
            href={`mailto: ${
              process.env.REACT_APP_CUSTOMER_SERVICE_EMAIL
            }?Subject=${encodeURI('Account Permissions Request')}`}
          >
            contact customer service.{' '}
          </a>
          To try again, or log in to another account, press the "Login" button.
        </BottomContent>
        <ButtonContainer>
          <Button variant="contained" onClick={login} fullWidth>
            Login
          </Button>
        </ButtonContainer>
      </UnauthorizedDialog>
    </Dialog>
  )
}

export default UnauthorizedAccess
